import { FC } from 'react';

import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom } from '@ravago/shared/page-data/models/elements';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { getStatusBadgeVariant, getStatusTranslation } from '../../../utils/order/order-helper.utils';
import { OrderLineItem } from '../order/order-line-item';

import { OrderLineTableMenu } from './order-line-table-menu';

interface DesktopOrderLinesTableProps {
   orderLines: DisplayOrder[];
   locale: string;
   startIndex: number;
   endIndex: number;
   acceptWebp?: boolean;
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   onOrderLineButtonClick: (orderLine: DisplayOrder) => void;
}

export const DesktopOrderLinesTable: FC<DesktopOrderLinesTableProps> = ({
   orderLines,
   locale,
   acceptWebp,
   openedFrom,
   onOrderLineButtonClick,
   startIndex,
   endIndex,
   orderBaseUrls
}) => {
   const translate = useTranslations();
   const format = useFormatter();

   return (
      <Table rowDividers rounded border>
         {openedFrom === 'order_overview_order' && (
            <TableHead>
               <TableRow>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('my-orders.table.product')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('my-orders.order-header')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('my-orders.table.delivery')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('my-orders.table.status')}</Typography>
                  </TableHeader>
                  <TableHeader>{}</TableHeader>
               </TableRow>
            </TableHead>
         )}
         <TableBody>
            {orderLines
               .filter((_, index) => index >= startIndex && index < endIndex)
               .map((orderLine) => (
                  <TableRow key={orderLine.orderNumber} onClick={() => {}}>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)} width="260px">
                        <OrderLineItem order={orderLine} openedFrom={openedFrom} acceptWebp={acceptWebp} />
                     </TableCell>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)}>
                        <Box direction="column" gap="3xs">
                           <Typography variant="small">
                              {translate('my-orders.table.order-line')} {orderLine.orderNumber}
                           </Typography>
                           <Typography variant="small">
                              {translate('my-orders.table.order-ref')} {orderLine.customerOrderReference || '-'}
                           </Typography>
                        </Box>
                     </TableCell>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)}>
                        <Box direction="column" gap="none">
                           {orderLine.isCallOff ? (
                              <>
                                 <Typography variant="small-highlight">
                                    {translate(orderLine.status === 'Closed' ? 'my-orders.delivered' : 'my-orders.call-off-period')}
                                 </Typography>
                                 <Typography variant="small">
                                    {orderLine.deliveryDate ? DateUtils.transformCallOffOrderPeriod(format, orderLine.deliveryDate) : '-'}
                                 </Typography>
                              </>
                           ) : (
                              <>
                                 <Typography variant="small-highlight">
                                    {translate(orderLine.status === 'Closed' ? 'my-orders.delivered' : 'my-orders.estimated-delivery')}
                                 </Typography>
                                 <Typography variant="small">
                                    {orderLine.deliveryDate ? DateUtils.transformOrderDeliveryDate(format, orderLine.deliveryDate) : '-'}
                                 </Typography>
                              </>
                           )}
                        </Box>
                     </TableCell>
                     <TableCell onClick={() => onOrderLineButtonClick(orderLine)}>
                        <Badge size="regular" variant={getStatusBadgeVariant(orderLine)} weight="bold">
                           {translate(getStatusTranslation(orderLine.status))}
                        </Badge>
                     </TableCell>
                     <TableCell>
                        <Box justify="end">
                           <OrderLineTableMenu
                              orderLine={orderLine}
                              locale={locale}
                              openedFrom={openedFrom}
                              onOrderLineButtonClick={onOrderLineButtonClick}
                              orderBaseUrls={orderBaseUrls}
                           />
                        </Box>
                     </TableCell>
                  </TableRow>
               ))}
         </TableBody>
      </Table>
   );
};
